import { FC } from 'react';
import { Space, Collapse, Row, Col, Typography, Table, Tag } from 'antd';
import { SurgeryData, SurgeryItem } from 'pages/surgery/types/SurgeryType';
import { useTranslation } from 'react-i18next';

const { Panel } = Collapse;
const { Text } = Typography;

const surgeryTypeColor: Record<string, string> = {
  Minor: '',
  Intermediate: 'orange',
  Major: 'red',
  Complex: 'magenta',
};

const columns = [
  {
    dataIndex: 'type',
    key: 'type',
    width: '10%',
    render: (type: string, { key }: { key: string }) => {
      let style = undefined;
      if (type === 'Minor') {
        style = {
          backgroundColor: '#fff',
          borderStyle: 'dashed',
        };
      }

      return (
        <Tag color={surgeryTypeColor[type]} key={`${key}-${type}`} style={style}>
          {type}
        </Tag>
      );
    },
  },
  {
    dataIndex: 'info',
    key: 'info',
    render: (info: string, { remark }: { remark?: string }) => {
      return remark ? (
        <Space direction="vertical">
          <Text>{info}</Text>
          {remark !== '' && <Text type="secondary">{remark}</Text>}
        </Space>
      ) : (
        <Text>{info}</Text>
      );
    },
  },
];

interface SurgeryListProps {
  data: SurgeryData[];
}

const SurgeryList: FC<SurgeryListProps> = ({ data }) => {
  const { t } = useTranslation();

  const headerItem = (item: SurgeryData) => {
    return (
      <Space align="baseline">
        <Text strong>{item.title}</Text>
        <Text style={{ color: 'var(--neutral-7)' }}>{item.subtitle}</Text>
      </Space>
    );
  };

  const getDataSource = (item: SurgeryData) => {
    const data = [];

    data.push(
      ...item.minorSurgeries.map((surgery: SurgeryItem) => ({
        key: surgery.id,
        type: t('benefits.surgery.minor'),
        info: surgery.title,
        remark: surgery.info?.body,
      })),
    );
    data.push(
      ...(item.intermediateSurgeries || []).map((surgery: SurgeryItem) => ({
        key: surgery.id,
        type: t('benefits.surgery.intermediate'),
        info: surgery.title,
        remark: surgery.info?.body,
      })),
    );
    data.push(
      ...(item.majorSurgeries || []).map((surgery: SurgeryItem) => ({
        key: surgery.id,
        type: t('benefits.surgery.major'),
        info: surgery.title,
        remark: surgery.info?.body,
      })),
    );
    data.push(
      ...(item.complexSurgeries || []).map((surgery: SurgeryItem) => ({
        key: surgery.id,
        type: t('benefits.surgery.complex'),
        info: surgery.title,
        remark: surgery.info?.body,
      })),
    );

    return data;
  };

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Collapse>
          {data.map((item: SurgeryData) => (
            <Panel header={headerItem(item)} key={item.id}>
              <Table showHeader={false} pagination={false} columns={columns} dataSource={getDataSource(item)} />
            </Panel>
          ))}
        </Collapse>
      </Col>
    </Row>
  );
};

export default SurgeryList;
